import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import StageFrame from './StageFrame';
import { useCycle } from './useCycle';

const list = (value: unknown): string[] => (Array.isArray(value) ? (value as string[]) : []);

const FactoryFlow: React.FC = () => {
  const { t } = useTranslation();
  const { phase, reduce, replay } = useCycle(7000);
  const solved = phase === 'solution';
  const silos = list(t('anim.factory.silos', { returnObjects: true }));
  const flow = list(t('anim.factory.flow', { returnObjects: true }));
  const siloNotes = list(t('anim.factory.siloNotes', { returnObjects: true }));
  const flowNotes = list(t('anim.factory.flowNotes', { returnObjects: true }));
  const problemCaption = t('anim.factory.problemCaption');
  const solutionCaption = t('anim.factory.solutionCaption');

  const columns = (linked: boolean) => {
    const titles = linked ? flow : silos;
    const notes = linked ? flowNotes : siloNotes;
    return (
      <div className="relative">
        <div className="grid grid-cols-3 gap-3 sm:gap-5">
          {titles.map((title, index) => (
            <motion.article
              key={`${linked ? 'flow' : 'silo'}-${title}`}
              initial={reduce ? false : { opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: reduce ? 0 : index * 0.12 }}
              className={`rounded-xl border px-3 py-4 ${
                linked ? 'border-mint/40 bg-mint-dim' : 'border-coral/35 bg-[#1a1216]'
              }`}
            >
              <p className={`font-mono text-[0.65rem] uppercase tracking-wide ${linked ? 'text-mint' : 'text-coral'}`}>
                0{index + 1}
              </p>
              <h3 className="mt-2 font-display text-lg text-paper sm:text-xl">{title}</h3>
              <p className="mt-2 text-xs text-muted sm:text-sm">{notes[index]}</p>
            </motion.article>
          ))}
        </div>
        <div className="pointer-events-none absolute left-[16%] right-[16%] top-1/2 hidden h-px sm:block" aria-hidden="true">
          <div className={`h-px w-full ${linked ? 'bg-mint/70' : 'border-t border-dashed border-coral/50'}`} />
          {linked && !reduce && (
            <motion.span
              className="absolute -top-1 h-2 w-2 rounded-full bg-amber"
              initial={{ left: '0%' }}
              animate={{ left: '100%' }}
              transition={{ duration: 1.6, repeat: Infinity, ease: 'linear' }}
            />
          )}
        </div>
      </div>
    );
  };

  return (
    <StageFrame
      kicker={t('anim.factory.kicker')}
      phase={reduce ? 'solution' : phase}
      caption={`${problemCaption} ${solutionCaption}`}
      onReplay={replay}
    >
      {reduce ? (
        <div className="space-y-8">
          {columns(false)}
          {columns(true)}
        </div>
      ) : (
        columns(solved)
      )}
    </StageFrame>
  );
};

export default FactoryFlow;
